import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Circle, Sparkles, X, ChevronDown, ChevronUp } from 'lucide-react';
import { axiosInstance } from '../App';

/**
 * FoundersChecklist — Iter 120 (Feb 2026)
 *
 * Lightweight "first-week success" checklist shown to businesses on the
 * Founding tier. Auto-detects completion against backend state (no manual
 * ticking), persists dismissal across reloads, and switches to a celebration
 * state when all 5 required items are complete.
 *
 * Design priorities (per user spec):
 *   - feel encouraging, NOT pressuring
 *   - mobile-friendly (collapsible, large tap targets)
 *   - non-intrusive (dismissable; hides for non-founders)
 *   - visual progress (% + completion ticks)
 */

// Friendly route nudges: when the user taps an incomplete item, send them to
// the right part of the dashboard / profile so the path to completion is
// obvious. Used as anchors / smooth-scroll targets where possible.
const ITEM_NUDGES = {
  profile_complete:  { scroll: 'business-profile-section' },
  photos_3plus:      { scroll: 'business-profile-section' },
  first_event:       { scroll: 'events-section' },
  first_offer:       { scroll: 'offers-section' },
  first_follower:    { scroll: null },
  opening_hours:     { scroll: 'business-profile-section' },
  amenities:         { scroll: 'business-profile-section' },
  saved_card:        { scroll: null },
};

const ITEM_ORDER = [
  'profile_complete',
  'photos_3plus',
  'first_event',
  'first_offer',
  'first_follower',
  'opening_hours',
  'amenities',
  'saved_card',
];

const FoundersChecklist = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [celebrated, setCelebrated] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/business/me/activation');
      setData(res.data);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Show a one-shot celebration the first time we hit isComplete on the
  // client. Backend separately stamps `activationCompletedAt` so admins can
  // see when each founder activated.
  useEffect(() => {
    if (!data) return;
    if (data.isComplete && !celebrated) {
      const seen = (() => { try { return localStorage.getItem('foundersChecklistCelebrated') === '1'; } catch { return false; } })();
      if (!seen) {
        setCelebrated(true);
        try { localStorage.setItem('foundersChecklistCelebrated', '1'); } catch { /* ignore */ }
      }
    }
  }, [data, celebrated]);

  if (!data) return null;
  if (data.subscriptionPlan !== 'founders') return null;
  if (data.dismissed) return null;

  const { items, completedRequired, totalRequired, isComplete } = data;
  const pct = Math.round((completedRequired / Math.max(totalRequired, 1)) * 100);

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await axiosInstance.post('/business/me/activation/dismiss');
      setData({ ...data, dismissed: true });
    } catch {
      setDismissing(false);
    }
  };

  const goTo = (key) => {
    const nudge = ITEM_NUDGES[key];
    if (!nudge?.scroll) return;
    const el = document.getElementById(nudge.scroll);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const ordered = ITEM_ORDER
    .map(k => items.find(i => i.key === k))
    .filter(Boolean);

  return (
    <div
      className="bg-white border border-amber-200 rounded-2xl shadow-sm overflow-hidden"
      data-testid="founders-checklist"
    >
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-400 flex items-center justify-center flex-shrink-0">
          <Sparkles className="h-4.5 w-4.5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-900">
            {isComplete
              ? t('foundersChecklist.titleComplete', 'You are all set up — nicely done!')
              : t('foundersChecklist.title', 'Your first-week success checklist')}
          </p>
          <p className="text-xs text-amber-800/80 truncate">
            {isComplete
              ? t('foundersChecklist.subtitleComplete', 'Your business is ready to attract customers.')
              : t('foundersChecklist.subtitle', 'Quick wins that help local customers find you.')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          className="p-1.5 rounded-lg hover:bg-amber-100 text-amber-900"
          data-testid="founders-checklist-toggle"
          aria-label={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          disabled={dismissing}
          className="p-1.5 rounded-lg hover:bg-amber-100 text-amber-900 disabled:opacity-50"
          data-testid="founders-checklist-dismiss"
          aria-label={t('foundersChecklist.dismiss', 'Hide checklist')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="px-4 pt-3">
        <div className="flex items-center justify-between text-xs text-gray-600 mb-1.5">
          <span data-testid="founders-checklist-count">
            {completedRequired} / {totalRequired} {t('foundersChecklist.completed', 'completed')}
          </span>
          <span className="font-semibold text-amber-700" data-testid="founders-checklist-pct">{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-amber-100 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-amber-400 to-orange-400 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Body */}
      {!collapsed && (
        <div className="px-2 sm:px-3 py-3 space-y-1">
          {ordered.map((item) => {
            const Icon = item.done ? CheckCircle2 : Circle;
            return (
              <button
                type="button"
                key={item.key}
                onClick={() => !item.done && goTo(item.key)}
                disabled={item.done}
                className={`w-full flex items-start gap-3 px-2 py-2 rounded-lg text-left transition-colors ${
                  item.done
                    ? 'cursor-default'
                    : 'hover:bg-amber-50 active:bg-amber-100'
                }`}
                data-testid={`founders-checklist-item-${item.key}`}
              >
                <Icon
                  className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                    item.done ? 'text-emerald-500' : 'text-gray-300'
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${item.done ? 'text-gray-500 line-through' : 'text-gray-800 font-medium'}`}>
                    {t(`foundersChecklist.items.${item.key}.label`, _defaultLabel(item.key))}
                    {!item.required && (
                      <span className="ml-2 inline-block text-[10px] uppercase tracking-wide text-amber-700/80 font-semibold">
                        {t('foundersChecklist.bonus', 'Bonus')}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 leading-snug mt-0.5">
                    {t(`foundersChecklist.items.${item.key}.hint`, _defaultHint(item.key))}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Celebration footer */}
      {isComplete && !collapsed && (
        <div
          className="px-4 py-3 bg-emerald-50 border-t border-emerald-200 flex items-center gap-2"
          data-testid="founders-checklist-celebration"
        >
          <Sparkles className="h-4 w-4 text-emerald-600 flex-shrink-0" />
          <p className="text-xs text-emerald-800">
            {t('foundersChecklist.celebration', 'Great work — you have unlocked the most effective parts of your listing. Customers in your region will start noticing you.')}
          </p>
        </div>
      )}
    </div>
  );
};

// Default copy used when the i18n key is missing — keeps the component
// self-sufficient if a translation file slips out of sync.
function _defaultLabel(key) {
  return {
    profile_complete: 'Complete your business profile',
    photos_3plus:     'Upload at least 3 photos',
    first_event:      'Publish your first event',
    first_offer:      'Publish your first offer',
    first_follower:   'Gain your first follower',
    opening_hours:    'Add opening hours',
    amenities:        'Add amenities or services',
    saved_card:       'Save a card for one-click boosts',
  }[key] || key;
}

function _defaultHint(key) {
  return {
    profile_complete: 'Description, phone and address — the basics customers expect.',
    photos_3plus:     'Listings with photos get many more views.',
    first_event:      'Something happening this week? Post it.',
    first_offer:      'A simple discount or special draws first-time customers.',
    first_follower:   'Share your listing link with a happy customer.',
    opening_hours:    'Help locals know when to drop by.',
    amenities:        'Group sizes, dietary options or accessibility info.',
    saved_card:       'Optional — only used if you decide to boost a post later.',
  }[key] || '';
}

export default FoundersChecklist;
